/**
 * Seletor de sabores para kits de sopa.
 * Soma quantidades até o total do kit e grava em properties[Sabores das sopas].
 */
class DtxSoupKitFlavors extends HTMLElement {
  /** @type {number} */
  #slots = 10;

  /** @type {HTMLInputElement | null} */
  #summaryInput = null;

  /** @type {HTMLElement | null} */
  #statusEl = null;

  /** @type {NodeListOf<HTMLInputElement>} */
  #qtyInputs;

  /** @type {boolean} */
  #productAvailable = true;

  connectedCallback() {
    this.#slots = Number(this.dataset.slots) || 10;
    this.#summaryInput = this.querySelector('[data-soup-kit-summary]');
    this.#statusEl = this.querySelector('[data-soup-kit-status]');
    this.#qtyInputs = this.querySelectorAll('[data-soup-kit-qty]');
    this.#productAvailable = this.dataset.productAvailable !== 'false';

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onChange);

    const form = this.#getProductForm();
    form?.addEventListener('submit', this.#onFormSubmit, true);

    this.#ensureKitVariant();
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('input', this.#onChange);

    const form = this.#getProductForm();
    form?.removeEventListener('submit', this.#onFormSubmit, true);
  }

  /** Garante que o form usa uma variante disponível (sabores vão em properties). */
  #ensureKitVariant() {
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const form = this.#getProductForm();
    const variantInput = form?.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;
      variantInput.disabled = false;
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-soup-kit-minus], [data-soup-kit-plus]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    event.preventDefault();

    const row = button.closest('[data-soup-kit-row]');
    if (row?.dataset.available === 'false') return;

    const input = row?.querySelector('[data-soup-kit-qty]');
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    const current = Number(input.value) || 0;
    const total = this.#total();
    const flavorMax = Number(input.dataset.flavorMax || input.max || this.#slots);

    if (button.hasAttribute('data-soup-kit-plus')) {
      if (total >= this.#slots || current >= flavorMax) return;
      input.value = String(current + 1);
    } else {
      input.value = String(Math.max(0, current - 1));
    }

    this.#sync();
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.hasAttribute('data-soup-kit-qty')) return;
    if (target.disabled || target.dataset.available === 'false') {
      target.value = '0';
      this.#sync();
      return;
    }

    let value = Math.max(0, Math.floor(Number(target.value) || 0));
    const others = this.#totalExcept(target);
    const flavorMax = Number(target.dataset.flavorMax || target.max || this.#slots);
    const maxForThis = Math.max(0, Math.min(flavorMax, this.#slots - others));
    if (value > maxForThis) value = maxForThis;
    target.value = String(value);
    this.#sync();
  };

  /** @param {Event} event */
  #onFormSubmit = (event) => {
    if (this.#total() !== this.#slots) {
      event.preventDefault();
      event.stopPropagation();
      this.#sync();
      this.#statusEl?.focus?.();
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  #total() {
    let sum = 0;
    this.#qtyInputs.forEach((input) => {
      sum += Number(input.value) || 0;
    });
    return sum;
  }

  /** @param {HTMLInputElement} except */
  #totalExcept(except) {
    let sum = 0;
    this.#qtyInputs.forEach((input) => {
      if (input === except) return;
      sum += Number(input.value) || 0;
    });
    return sum;
  }

  #sync() {
    const total = this.#total();
    const remaining = this.#slots - total;
    const complete = total === this.#slots;

    const parts = [];
    this.#qtyInputs.forEach((input) => {
      if (input.disabled || input.dataset.available === 'false') {
        input.value = '0';
        return;
      }

      const qty = Number(input.value) || 0;
      const flavor = input.dataset.flavor || '';
      if (qty > 0 && flavor) parts.push(`${flavor} (${qty})`);

      const row = input.closest('[data-soup-kit-row]');
      row?.classList.toggle('is-active', qty > 0);
      const flavorMax = Number(input.dataset.flavorMax || input.max || this.#slots);
      const minus = row?.querySelector('[data-soup-kit-minus]');
      const plus = row?.querySelector('[data-soup-kit-plus]');
      if (minus instanceof HTMLButtonElement) minus.disabled = qty <= 0;
      if (plus instanceof HTMLButtonElement) {
        plus.disabled = remaining <= 0 || qty >= flavorMax;
      }
    });

    if (this.#summaryInput) {
      this.#summaryInput.value = complete ? parts.join(', ') : '';
      this.#summaryInput.setAttribute('aria-invalid', complete ? 'false' : 'true');
    }

    this.classList.toggle('is-complete', complete);
    this.classList.toggle('is-incomplete', !complete);

    if (this.#statusEl) {
      this.#statusEl.textContent = `${total} / ${this.#slots}`;
      if (complete) {
        this.#statusEl.dataset.state = 'ok';
      } else if (total === 0) {
        this.#statusEl.dataset.state = 'empty';
      } else {
        this.#statusEl.dataset.state = 'progress';
      }
    }

    this.#ensureKitVariant();
    this.#setAddToCartEnabled(complete);
  }

  /** @param {boolean} enabled */
  #setAddToCartEnabled(enabled) {
    const form = this.#getProductForm();
    if (!form) return;

    const productForm = form.closest('product-form-component');
    const buttons = productForm
      ? productForm.querySelectorAll('add-to-cart-component button, [ref="addToCartButton"]')
      : form.querySelectorAll('[name="add"], [type="submit"]');

    const canSell = enabled && this.#productAvailable;

    buttons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      if (canSell) {
        button.removeAttribute('data-soup-kit-locked');
        button.disabled = false;
      } else {
        if (!button.hasAttribute('data-soup-kit-locked')) {
          button.dataset.soupKitWasDisabled = button.disabled ? 'true' : 'false';
        }
        button.setAttribute('data-soup-kit-locked', 'true');
        button.disabled = true;
      }
    });

    document.querySelectorAll('sticky-add-to-cart button[type="submit"], sticky-add-to-cart [ref="addToCartButton"]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      if (canSell) {
        button.disabled = false;
        button.removeAttribute('data-soup-kit-locked');
      } else {
        button.setAttribute('data-soup-kit-locked', 'true');
        button.disabled = true;
      }
    });
  }

  /** @returns {HTMLFormElement | null} */
  #getProductForm() {
    const formId = this.dataset.formId;
    if (formId) {
      const byId = document.getElementById(formId);
      if (byId instanceof HTMLFormElement) return byId;
    }

    const sectionId = this.dataset.sectionId;
    if (sectionId) {
      const byName = document.getElementById(`BuyButtons-ProductForm-${sectionId}`);
      if (byName instanceof HTMLFormElement) return byName;
    }

    return document.querySelector('product-form-component form[data-type="add-to-cart-form"]');
  }
}

if (!customElements.get('dtx-soup-kit-flavors')) {
  customElements.define('dtx-soup-kit-flavors', DtxSoupKitFlavors);
}
